import React, { Component } from "react";
import { EjariProperty, EjariPropertyType } from "@eres/ejari-api/Entity";
import { convertSqMToSqFt, ImperialSystem, UnitSystem } from "@my-dld/utils/UnitUtils";
import Strings from "@my-dld/assets/strings";
import { CurrencyUtil, LocaleUtil, StringUtil } from "@eres/core";
import { DataTableLang } from "@my-dld/utils/DataTableUtils";
import { downloadTenancyContract } from "@my-dld/actions/PropertyActions";
import { normalizedPropertyId } from "@eres/ejari-api/Utils";
import { navigateWithInParent } from "@my-dld/utils/StoreUtils";
import { resetTenantContext, updateTenantContext } from "@eres/ejari-core/contexts/TenantContext";
import { canDoContractOperations } from "@eres/ejari-api/EjariPropertyUtil";
import ListDataView from "../DataView/ListDataView";
import { isEmptyStringOrNull } from "@eres/core/dist/StringUtils";
import { formatAppDate } from "@my-dld/utils/Utils";

export interface LPProps {
	properties: EjariProperty[];
	type: EjariPropertyType;
	unit: UnitSystem;
}

class LeaseTable extends Component<LPProps> {
	componentDidMount(): void {
		this.configDataTable();
	}

	configDataTable = () => {
		const { type } = this.props;
		try {
			window.$( "#my-prop-table-" + type ).DataTable( {
                language: LocaleUtil.Select(DataTableLang),
                dom: "<'table-responsive'rt>" + "<'dt-cfooter'i<'dt-fpl'pl>>",
				//responsive: false,
				lengthChange: false,
				scrollX: false
			} );
			const openDetails = this.openDetails;
			window.$( "#my-prop-table-" + type ).on( "click", "a", function () {
				// @ts-ignore
				let id = this.getAttribute( "id" );
				if ( id === "download" ) {
					// @ts-ignore
					let p = JSON.parse( this.getAttribute( "data-property" ) );
					if ( p )
						downloadTenancyContract( p );
				} else if ( id === "details" ) {
					// @ts-ignore
					let item = JSON.parse( this.getAttribute( "data-item" ) );
					openDetails( item );
				}
				return false;
			} );
		} catch ( e ) {
			console.warn( "Data table unavailable", e );
		}
	};

	openDetails = ( property: EjariProperty ) => {
		resetTenantContext();
		updateTenantContext( { property: property } );
		navigateWithInParent( "mydld", "tenant/lease/details" );
	};

	render() {
		const { properties, type } = this.props;
		if ( properties == null || properties.length < 1 ) {
			return null;
		}
		let Header = LandHeader;
		let Row = LandRow;
		switch ( type ) {
			case EjariPropertyType.VIRTUAL_VILLA:
			case EjariPropertyType.VILLA:
			case EjariPropertyType.BUILDING:
				Header = VillaHeader;
				Row = VillaRow;
				break;
			case EjariPropertyType.UNIT:
				Header = UnitHeader;
				Row = UnitRow;
				break;
		}
        return <ListDataView
			id={ "my-prop-table-" + type }
			title={ `${ getLPLabel( type ) } (${ properties!.length })` }
			items={ properties ? properties : [] }
			renderListHeader={ () => <Header/> }
			renderListItem={ ( value: EjariProperty, index: number ) => {
				return <Row
					key={ normalizedPropertyId( value ) + "-" + index }
					isSqFt={ this.props.unit.Id === ImperialSystem.Id }
					property={ value }
					onDetails={ this.openDetails }
					onDownload={ downloadTenancyContract }/>;
            }} />;
	}
}

export function getLPLabel( t: EjariPropertyType ) {
	switch ( t ) {
		case EjariPropertyType.LAND:
		case EjariPropertyType.COMPOUND:
			return Strings.land_label;
		case EjariPropertyType.BUILDING:
			return Strings.building_label;
		case EjariPropertyType.UNIT:
			return Strings.unit;
		case EjariPropertyType.VILLA:
		case EjariPropertyType.VIRTUAL_VILLA:
			return Strings.villa;
	}
}

interface RowProps {
	property: EjariProperty;
	onDetails: ( p: EjariProperty ) => void;
	onDownload: ( p: EjariProperty ) => void;
	isSqFt: boolean;
}

function LandHeader() {
	return <tr>
		<LH/>
	</tr>;
}

function LandRow( props: RowProps ) {
	return <tr>
		<LR { ...props } xClass={ "underline" }/>
	</tr>;
}

function LH() {
	return <React.Fragment>
		<th>{ Strings.land_num_label }</th>
		<th>{ Strings.area_label }</th>
		<th>{ Strings.size }</th>
		<th>{ Strings.contract_status }</th>
		<th>{ Strings.contract_num_sign }</th>
		<th>{ Strings.contract_amount_aed }</th>
		<th>{ Strings.contract_end_date_label }</th>
		<th>{ Strings.certificate }</th>
		<th>{ "" }</th>
	</React.Fragment>;
}

function TD( props: { value?: string, className?: string, onClick?: () => void; label?: string } ) {
	let { value, className, label } = props;
	if ( value == null || StringUtil.isEmptyStringOrNull( value ) )
		value = "-";
	return <td
		data-label={ label } className={ className } onClick={ e => {
		e.preventDefault();
		if ( props.onClick ) props.onClick();
	} }>{ value }</td>;
}

function LR( props: RowProps & { xClass?: string, canDoOperations?: boolean } ) {
	const { property, isSqFt, canDoOperations } = props;
	let { xClass } = props;
	if ( xClass === "underline" ) {
		xClass = xClass + " cur-hand";
	}
	return <React.Fragment>
		<TD
			label={ Strings.land_num_label } className={ "prop-mun-no " + ( xClass ? xClass : "" ) }
			value={ getLPMunNo( property ) }/>
		<TD label={ Strings.area_label } value={ LocaleUtil.getLocalValue( property.Area ) }/>
		<TD label={ Strings.size } className="size" value={ getLPSize( property, isSqFt ) }/>
		<TD
			label={ Strings.contract_status } className="prop-status"
			value={ LocaleUtil.getLocalValue( property.ContractStatusName ) }/>
		<TD label={ Strings.contract_num_sign } className="prop-mun-no" value={ getLPContractNo( property ) }/>
		<TD label={ Strings.contract_amount_aed } className="currency" value={ getLPContractAmount( property ) }/>
		<TD label={ Strings.contract_end_date_label } className="prop-mun-no" value={ getLPContractEndDate( property ) }/>
		<td data-label={ Strings.certificate } className="props-actions align-self-center">
			{ canDoOperations ? <a
				id={ "download" }
                className={ "cur-hand underline" }
				data-property={ JSON.stringify( {
					CurrentContract: {
						ContractNumber: property.CurrentContract.ContractNumber,
						ContractKey: property.CurrentContract.ContractKey
					}
				} ) }
				/*onClick={ e => {
					e.preventDefault();
					props.onDownload(property);
				} }*/
			>
				{ Strings.download_label }
			</a> : <span>{ Strings.not_available }</span> }
		</td>
        <td className="ws-nowrap">
			<a
				id={ "details" }
                className={ "cur-hand underline" }
				data-item={ JSON.stringify( property ) }>
				{ Strings.view_details }
			</a>
		</td>
	</React.Fragment>;
}

function VillaHeader() {
	return <tr>
		<th>{ Strings.name }</th>
		<LH/>
	</tr>;
}

function VillaRow( props: RowProps ) {
	let property = props.property;
	return <tr>
		<TD
			label={ Strings.name } className={ "prop-mun-no" } value={ LocaleUtil.getLocalValue( property.BuildingName ) }
			/*onClick={ () => {
				props.onDetails(props.property);
			} }*//>
		<LR { ...props } canDoOperations={ canDoContractOperations( property.CurrentContract ) }/>
	</tr>;
}

function UnitHeader() {
	return <tr>
		<th>{ Strings.Unit_Number_Label }</th>
		<th>{ Strings.building_name }</th>
		<LH/>
	</tr>;
}

function UnitRow( props: RowProps ) {
	let property = props.property;
	// let val = getUnitNumber(property);
	return <tr>
		<TD
			label={ Strings.Unit_Number_Label } className="prop-unit-no underline  cur-hand" value={ property.UnitNo }
			/*onClick={ () => {
				props.onDetails(props.property);
			} }*//>
		<TD
			label={ Strings.building_name } className={ "prop-b-name" }
			value={ LocaleUtil.getLocalValue( property.BuildingName ) }/>
		<LR { ...props } canDoOperations={ canDoContractOperations( property.CurrentContract ) }/>
	</tr>;
}

export function getLPMunNo( property: EjariProperty ) {
	let landNo = property.LandDmNo;
	const landSubNo = property.LandDmSubNo;
	if ( !StringUtil.isEmptyStringOrNull( landSubNo ) ) {
		landNo += "-" + landSubNo;
	}
	return landNo;
}

export function getLPSize( property: EjariProperty, isSqFt: boolean ) {
	return ( isSqFt ? convertSqMToSqFt( Number( property.Size ) ) : property.Size ) + "";
}

export function getLPContractNo( property: EjariProperty ) {
	return property.CurrentContract.ContractNumber;
}

export function getLPContractAmount( property: EjariProperty ) {
	const contract = property.CurrentContract;
	let contractAmt: string | number | undefined = contract.ContractAmount;
	if ( contractAmt == null || contractAmt <= 0 )
		contractAmt = "";
	else {
		contractAmt = CurrencyUtil.toFormattedCurrency( Number( contractAmt ) );
	}
	return !isEmptyStringOrNull( contractAmt ) ? contractAmt.substr( 0, contractAmt.lastIndexOf( "." ) ) : "-";
}

export function getLPContractEndDate( property: EjariProperty ) {
	let cd = "";
	const contract = property.CurrentContract;
	if ( contract.ContractEndDate ) {
		cd = formatAppDate( contract.ContractEndDate?.asDate() );
	}
	return !isEmptyStringOrNull( cd ) ? cd : "-";
}

export default LeaseTable;
